import { useSortable } from '@dnd-kit/sortable';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { ArrowDownTrayIcon, ChevronDownIcon, ChevronRightIcon, FolderMinusIcon, FolderPlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { MouseEventHandler, memo, useCallback, useContext, useMemo } from 'react';
import { useT } from '../../../../translation/src';
import { useAnnotationIcon, type AnnotationTypeName } from '../../../../utils/annotationIcons';
import { ContextMenu } from '../../../../components/ContextMenu';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { useIsMultiSelect } from '../../../../hooks/modules/renderer/useIsMultiSelect';
import { useRename } from '../../../../hooks/modules/renderer/useRename';
import { useRenameSubmit } from '../../../../hooks/modules/renderer/useRenameSubmit';
import { useUpdatePosition } from '../../../../hooks/modules/renderer/useUpdatePosition';
import { useDeleteAnnotation } from '../../../../hooks/potree/useDeleteAnnotation';
import { useGroupOperations } from '../../../../hooks/potree/useGroupOperations';
import useOpen from '../../../../hooks/useOpen';
import { useCustomSelector } from '../../../../redux/store';
import { useSelector } from 'react-redux';
import { T } from '../../../../translation/src';
import { Project, Annotation as TypeAnnotation, GroupType } from '../../../../types/graphqlTypes';
import { AnnotationGroupModal } from '../../../project/AnnotationGroupModal';
import { MoveToGroupSubmenu } from './MoveToGroupSubmenu';
import { AnnotationsEyeIcon, AnnotationsFocusIcon } from './AnnotationsIconsWrapper';
import OrthoviewIcon from '../../../../assets/icons/orthoview.svg?react';
import { useViewManager } from '../../../../hooks/useViewManager';
import { RendererContext } from '../../../../contexts/RendererContext';
import { useToggleHide } from '../../../../hooks/modules/project/useToggleHide';

import { AnnotationsFilterIcon } from './FilterToggle';
import { WallSketchFilterToggle } from './WallSketchFilterToggle';
import { FILTERABLE_ANNOTATION_TYPES } from '../../../../utils/constants';
import { Pill } from '../../../../components/Pill';
import { useDeleteConfirmation } from '../../../../contexts/DeleteConfirmationContext';
import { AnnotationType, getAnnotationNameProperty, getAnnotationVisibleProperty, getDistancePointsProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../../Properties/reactive/usePropertyValue';
import { useExportDXF } from '../../../../hooks/potree/useExportDXF';
import { useExportIFC } from '../../../../hooks/potree/useExportIFC';
import { MultiSelectMenuItems } from './MultiSelectMenuItems';
import { useAnnotationLookup } from '../../../../hooks/potree/useAnnotationContext';
import { useFlatAnnotationList } from '../../../../hooks/potree/useFlatAnnotationList';
import { OpeningRow } from './OpeningRow';

export interface AnnotationProps {
  project?: Pick<Project, 'id'>;
  type: AnnotationTypeName;
  annotation: Pick<TypeAnnotation, 'identifier' | 'groupIdentifier' | 'type'> & {
    index: number;
  };
  active?: boolean;
  rootActive?: boolean;
  className?: string;
  groupIdentifier?: string;
  groupType?: GroupType;
  isFirst?: boolean;
  isLast?: boolean;
  onClickAnnotation: (args1: { identifier: string; type: 'group' | 'annotation'; groupIdentifier?: string; instanceKey?: string }, args2: { isCtrl?: boolean; isShift?: boolean }) => void;
  rootIndex: number;
  isReadOnly: boolean;
  isDragOver?: boolean;
  /** For ROOM annotations: identifiers of openings linked to this room (rendered as nested children). */
  linkedOpeningIds?: string[];
  /** For ROOM annotations: whether the nested openings list is expanded. */
  isRoomOpen?: boolean;
  /** Called when the chevron on a room is clicked. */
  onRoomOpenChange?: (roomId: string, isOpen: boolean) => void;
}

const isLockedToGroup = (ann: Pick<TypeAnnotation, 'type'>) => ann.type === 'ROOM' || ann.type === 'WALL_SKETCH';

// ============================================================================
// LAZY CONTEXT MENU CONTENT — only mounts when menu is open
// ============================================================================

interface AnnotationMenuContentProps {
  project?: Pick<Project, 'id'>;
  annotation: AnnotationProps['annotation'];
  groupIdentifier?: string;
  isFirst?: boolean;
  isLast?: boolean;
  rootIndex: number;
  onToggleNameEditable: (() => void) | undefined;
}

const AnnotationMenuContent: React.FC<AnnotationMenuContentProps> = ({ annotation, groupIdentifier, isFirst, isLast, rootIndex, onToggleNameEditable }) => {
  const annotationsByIdentifier = useAnnotationLookup();
  const { allSelectedAnnotations } = useFlatAnnotationList();
  const identifier = annotation.identifier;
  const { selectedAnnotations: selectedAnnotationsAndGroups } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const isMultiSelect = useIsMultiSelect() && selectedAnnotationsAndGroups.some((selected) => selected.identifier === identifier);
  const { onClose: closeCreateGroupModal, onOpen: openCreateGroupModal, open: createGroupModalOpen } = useOpen();
  const { deleteAnnotation, deleteWallSketchWithCascade } = useDeleteAnnotation();
  const wallSketchDeleteWarning = useT('Deleting the wall sketch also deletes all room folders and sketched objects on this floor. Room as-builts are kept. Are you sure?');
  const { moveAnnotationsToGroup } = useGroupOperations();
  const [{ UpdatePositionItems }] = useUpdatePosition({
    annotationIdentifier: identifier,
    parentGroupIdentifier: groupIdentifier,
    downDisabled: isLast,
    upDisabled: isFirst,
  });
  const { in2DMode, toggle2DMode, planeId: planeId2DMode } = useViewManager();
  const { toggleHide } = useToggleHide();
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const { requestDeleteConfirmation, requiresConfirmation } = useDeleteConfirmation();
  const [exportDXF, onCanExportDXF] = useExportDXF();
  const [exportIFC, onCanExportIFC] = useExportIFC();
  const reactiveVisible = usePropertyValue(getAnnotationVisibleProperty(identifier));
  const distancePoints = annotation.type === 'DISTANCE' ? getDistancePointsProperty(identifier).value : [];

  const annotationIdentifiers = useMemo(() => (isMultiSelect ? selectedAnnotationsAndGroups.map((a) => a.identifier) : [identifier]), [selectedAnnotationsAndGroups, identifier, isMultiSelect]);

  const onDeleteAnnotation = useCallback(() => {
    // A wall sketch tears down the floor's sketch-derived content with it:
    // rooms + room folders, sketch objects and opening sketches.
    if (annotation.type === 'WALL_SKETCH') {
      deleteWallSketchWithCascade(identifier);
    } else {
      deleteAnnotation(identifier);
    }
  }, [annotation.type, deleteAnnotation, deleteWallSketchWithCascade, identifier]);

  const onDeleteAnnotationWithConfirm = useCallback(() => {
    // Use the raw annotation type (e.g. 'OPENING') for confirmation lookup,
    // not the icon key (which splits OPENING into 'DOOR' / 'WINDOW').
    if (requiresConfirmation(annotation.type as AnnotationType)) {
      requestDeleteConfirmation(onDeleteAnnotation, annotation.type === 'WALL_SKETCH' ? wallSketchDeleteWarning : undefined);
    } else {
      onDeleteAnnotation();
    }
  }, [annotation.type, requiresConfirmation, requestDeleteConfirmation, onDeleteAnnotation, wallSketchDeleteWarning]);

  const onMoveAnnotationOutGroup = useCallback(() => {
    moveAnnotationsToGroup(annotationIdentifiers, null);
  }, [moveAnnotationsToGroup, annotationIdentifiers]);

  const exportAnnotations = useMemo(() => (isMultiSelect ? allSelectedAnnotations : [annotationsByIdentifier[identifier]]) as TypeAnnotation[], [isMultiSelect, allSelectedAnnotations, annotationsByIdentifier, identifier]);

  const onExportAnnotationsDXF = useCallback(() => {
    exportDXF(exportAnnotations);
  }, [exportDXF, exportAnnotations]);
  const onExportAnnotationsIFC = useCallback(() => {
    exportIFC(exportAnnotations);
  }, [exportIFC, exportAnnotations]);
  const dxfExportPossible = useMemo(() => onCanExportDXF(exportAnnotations), [onCanExportDXF, exportAnnotations]);
  const ifcExportPossible = useMemo(() => onCanExportIFC(exportAnnotations), [onCanExportIFC, exportAnnotations]);

  const selectionInfo = useMemo(
    () => ({
      hasLockedAnnotation: selectedAnnotationsAndGroups.some((selected) => {
        const ann = annotationsByIdentifier[selected.identifier];
        return ann && isLockedToGroup(ann);
      }),
      anyGroupLevelAnnotations: selectedAnnotationsAndGroups.some((a) => a.groupIdentifier),
    }),
    [selectedAnnotationsAndGroups, annotationsByIdentifier],
  );

  const groupMenuItem = useMemo(
    () => (
      <SelectItemContent icon={FolderPlusIcon} onClick={openCreateGroupModal}>
        <T _str="create group" swc />
      </SelectItemContent>
    ),
    [openCreateGroupModal],
  );
  const removeFromGroupMenuItem = useMemo(
    () => (
      <SelectItemContent icon={FolderMinusIcon} onClick={onMoveAnnotationOutGroup}>
        <T _str="remove from group" swc />
      </SelectItemContent>
    ),
    [onMoveAnnotationOutGroup],
  );

  const has2DAbility = annotation.type === 'DISTANCE' && distancePoints.length === 2;

  const computeIs2DAvailable = () => {
    if (!viewer?.personaManager) return false;
    const activePersona = viewer.personaManager.getActivePersona();
    if (activePersona?.name === 'floorplan') return false;
    const views = viewer.personaManager.viewManager?.getAvailableViews() || [];
    return views.some((view: { cameraMode?: number }) => view.cameraMode === 0);
  };

  const is2DModeAvailableInPersona = computeIs2DAvailable();

  const is2DContext = has2DAbility && in2DMode && planeId2DMode === identifier;

  const OrthoView = useMemo(
    () => (
      <SelectItemContent
        icon={OrthoviewIcon}
        onClick={() => {
          toggle2DMode(annotation.identifier);
          toggleHide({ identifiers: [annotation.identifier], visible: !!reactiveVisible });
        }}
      >
        {is2DContext ? <T _str="disable 2D mode" swc /> : <T _str="2D mode" swc />}
      </SelectItemContent>
    ),
    [toggle2DMode, toggleHide, annotation.identifier, reactiveVisible, is2DContext],
  );

  return (
    <>
      {isMultiSelect ? (
        <MultiSelectMenuItems>
          {!selectionInfo.hasLockedAnnotation && (
            <div>
              {groupMenuItem}
              <MoveToGroupSubmenu annotationIdentifiers={annotationIdentifiers} ignoreGroupIdentifier={annotation.groupIdentifier} />
              {selectionInfo.anyGroupLevelAnnotations && removeFromGroupMenuItem}
            </div>
          )}
        </MultiSelectMenuItems>
      ) : (
        <>
          <div className="flex flex-col">
            <SelectItemContent icon={PencilIcon} onClick={onToggleNameEditable}>
              <T _str="rename" swc />
            </SelectItemContent>
          </div>
          <div className="flex flex-col">
            <UpdatePositionItems index={rootIndex} />
          </div>
          {!isLockedToGroup(annotation) && (
            <div className="flex flex-col">
              {groupMenuItem}
              {groupIdentifier && removeFromGroupMenuItem}
              <MoveToGroupSubmenu annotationIdentifiers={annotationIdentifiers} ignoreGroupIdentifier={annotation.groupIdentifier} />
            </div>
          )}
          {has2DAbility && is2DModeAvailableInPersona && <div className="flex flex-col">{OrthoView}</div>}
          <div>
            <SelectItemContent icon={ArrowDownTrayIcon} onClick={onExportAnnotationsDXF} disabled={!dxfExportPossible}>
              <T _str="export to DXF" swc />
            </SelectItemContent>
            <SelectItemContent icon={ArrowDownTrayIcon} onClick={onExportAnnotationsIFC} disabled={!ifcExportPossible}>
              <T _str="export to IFC" swc />
            </SelectItemContent>
          </div>
          <div className="flex flex-col">
            <SelectItemContent icon={TrashIcon} onClick={onDeleteAnnotationWithConfirm}>
              <T _str="delete" swc />
            </SelectItemContent>
          </div>
        </>
      )}
      <AnnotationGroupModal onClose={closeCreateGroupModal} open={createGroupModalOpen} annotationIdentifiers={annotationIdentifiers} />
    </>
  );
};

// ============================================================================
// LIGHTWEIGHT ANNOTATION LIST ITEM
// ============================================================================

/** Inner content — does NOT call useSortable, so it won't re-render when the
 *  SortableContext items list changes. Only re-renders from its own props/hooks. */
const AnnotationContent = memo(
  ({
    project,
    type: _typeProp,
    annotation,
    className,
    rootActive,
    groupIdentifier,
    isFirst,
    isLast,
    onClickAnnotation,
    isReadOnly,
    rootIndex,
    isDragOver,
    linkedOpeningIds,
    isRoomOpen,
    onRoomOpenChange,
    sortableRef,
    sortableTransition,
    sortableAttributes,
    sortableListeners,
    isDragging,
  }: AnnotationProps & {
    sortableRef: (node: HTMLElement | null) => void;
    sortableTransition: string | undefined;
    sortableAttributes: DraggableAttributes;
    sortableListeners: DraggableSyntheticListeners;
    isDragging: boolean;
  }) => {
    const identifier = annotation.identifier;
    // Row icon — resolved reactively (subscribes to subtype changes, e.g.
    // opening kind) and per-object-type for sketch objects (chair, bath, …).
    const icon = useAnnotationIcon(annotation);
    // Fine-grained selector — returns boolean, only re-renders when THIS annotation's selection changes
    const active = useSelector((state: { rendererProvider: { selectedAnnotations: { identifier: string }[] } }) => state.rendererProvider.selectedAnnotations.some((a) => a.identifier === identifier));
    const submitRename = useRenameSubmit();

    const reactiveName = usePropertyValue(getAnnotationNameProperty(identifier));
    const reactiveVisible = usePropertyValue(getAnnotationVisibleProperty(identifier));
    const distancePoints = annotation.type === 'DISTANCE' ? getDistancePointsProperty(identifier).value : [];

    const [{ onToggleNameEditable }, { nameEditable, nameInputComponent }] = useRename({
      name: reactiveName,
      annotationIdentifier: annotation.identifier,
    });

    const onClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        submitRename();
        onClickAnnotation({ identifier, type: 'annotation', groupIdentifier }, { isCtrl: event.ctrlKey || event.metaKey, isShift: event.shiftKey });
      },
      [onClickAnnotation, identifier, submitRename, groupIdentifier],
    );

    const hasFilterAbility = FILTERABLE_ANNOTATION_TYPES.includes(annotation.type);

    const { in2DMode, planeId: planeId2DMode } = useViewManager();
    const has2DAbility = annotation.type === 'DISTANCE' && distancePoints.length === 2;
    const is2DContext = has2DAbility && in2DMode && planeId2DMode === identifier;

    // Rooms expand to show their linked openings as nested children. Chevron
    // is only rendered when there is at least one opening to show.
    const isRoom = annotation.type === 'ROOM';
    const hasOpenings = isRoom && !!linkedOpeningIds && linkedOpeningIds.length > 0;
    const onToggleRoomOpen = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        onRoomOpenChange?.(identifier, !isRoomOpen);
      },
      [identifier, isRoomOpen, onRoomOpenChange],
    );

    if (nameEditable) return nameInputComponent || null;

    // Hide the item being dragged - it appears in DragOverlay instead
    if (isDragging) {
      return null;
    }

    return (
      <>
        <ContextMenu
          popoverMenuDisabled={isReadOnly}
          buttonChild={
            <div
              ref={sortableRef}
              style={{ transition: sortableTransition }}
              {...sortableAttributes}
              {...sortableListeners}
              data-annotation-group-id={annotation.groupIdentifier}
              data-annotation-id={annotation.identifier}
              className="w-full"
            >
              <SelectItemContent
                onClick={onClick}
                icon={icon}
                active={rootActive || active || isDragOver}
                activeLevel={rootActive || isDragOver ? 1 : 0}
                selectStyle="primary"
                idle={!reactiveVisible}
                className={classNames(hasOpenings && 'relative', !className?.includes('pl-') && 'pl-8', className)}
                onDoubleClick={onToggleNameEditable}
              >
                {hasOpenings && (
                  <div
                    className={classNames('absolute flex items-center justify-center w-5 h-5 rounded hover:text-neon-green-400', groupIdentifier ? 'left-9' : 'left-1')}
                    onClick={onToggleRoomOpen}
                  >
                    {isRoomOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                  </div>
                )}
                <div className="overflow-hidden">{reactiveName}</div>
                <div className="mx-2 flex justify-end grow">{is2DContext && <Pill className="text-black bg-neon-green-500">2D</Pill>}</div>
                <div className="flex justify-end">
                  {hasFilterAbility && <AnnotationsFilterIcon project={project} annotation={annotation} />}
                  {annotation.type === 'WALL_SKETCH' && annotation.groupIdentifier && <WallSketchFilterToggle floorGroupId={annotation.groupIdentifier} />}
                  <AnnotationsFocusIcon identifier={annotation.identifier} visible={reactiveVisible} annotation={annotation} />
                  <AnnotationsEyeIcon identifier={annotation.identifier} visible={reactiveVisible} annotation={annotation} />
                </div>
              </SelectItemContent>
            </div>
          }
          panelClassName="divide-y"
        >
          <AnnotationMenuContent project={project} annotation={annotation} groupIdentifier={groupIdentifier} isFirst={isFirst} isLast={isLast} rootIndex={rootIndex} onToggleNameEditable={onToggleNameEditable} />
        </ContextMenu>
        {hasOpenings && isRoomOpen && (
          <div data-room-children={identifier} className="flow-root">
            {linkedOpeningIds.map((openingId) => (
              <OpeningRow
                key={`${identifier}-${openingId}`}
                identifier={openingId}
                roomIdentifier={identifier}
                isReadOnly={isReadOnly}
                onClickAnnotation={onClickAnnotation}
                className={groupIdentifier ? 'pl-20' : 'pl-16'}
              />
            ))}
          </div>
        )}
      </>
    );
  },
  (prev, next) =>
    prev.annotation.identifier === next.annotation.identifier &&
    prev.annotation.type === next.annotation.type &&
    prev.annotation.groupIdentifier === next.annotation.groupIdentifier &&
    prev.annotation.index === next.annotation.index &&
    prev.rootActive === next.rootActive &&
    prev.isFirst === next.isFirst &&
    prev.isLast === next.isLast &&
    prev.isReadOnly === next.isReadOnly &&
    prev.isDragOver === next.isDragOver &&
    prev.groupIdentifier === next.groupIdentifier &&
    prev.groupType === next.groupType &&
    prev.rootIndex === next.rootIndex &&
    prev.isDragging === next.isDragging &&
    prev.sortableTransition === next.sortableTransition &&
    prev.linkedOpeningIds === next.linkedOpeningIds &&
    prev.isRoomOpen === next.isRoomOpen &&
    prev.onRoomOpenChange === next.onRoomOpenChange &&
    prev.onClickAnnotation === next.onClickAnnotation,
);

/** Thin sortable wrapper — subscribes to SortableContext (dnd-kit) and passes
 *  sortable props down. When the items list changes, only this wrapper re-renders,
 *  not the heavy AnnotationContent tree. */
const Annotation_: React.FC2<AnnotationProps> = (props) => {
  const { attributes, listeners, setNodeRef, transition, isDragging } = useSortable({
    id: props.annotation.identifier,
    data: {
      type: 'annotation' as const,
      identifier: props.annotation.identifier,
      groupIdentifier: props.groupIdentifier,
      index: props.annotation.index,
    },
    disabled: props.isReadOnly,
  });

  return <AnnotationContent {...props} sortableRef={setNodeRef} sortableTransition={transition} sortableAttributes={attributes} sortableListeners={listeners} isDragging={isDragging} />;
};
export const Annotation = memo(Annotation_);
